import * as React from "react";
import { Typography, Box, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { MtUsageReportsList } from "./MtUsageReportsList";
import { useMutation, useQueryClient } from "react-query";
import { MetricsService, MtBillingType } from "gen/clients";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { Form, Formik } from "formik";
import { default as dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DATE_PICKER_FORMAT, DatePickerField } from "components/formikFields/DatePickerField/DatePickerField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { allMtBillingTypeOptions } from "pages/SystemSettingsPage/tabs/ApplcationsPage/utils/MtUtils";
import { SelectOption } from "@mui/base";
import AddIcon from "@mui/icons-material/Add";
import { MtUsageForClientBillingReportDetailsPage } from "./MtUsageForClientBillingReportDetailsPage/MtUsageForClientBillingReportDetailsPage";

dayjs.extend(utc);
dayjs.extend(timezone);

enum FieldNames {
  from = "from",
  to = "to",
  billingType = "billingType"
}

interface FormValues {
  [FieldNames.from]: string;
  [FieldNames.to]: string;
  [FieldNames.billingType]: MtBillingType | "";
}

const billingTypeOptions: SelectOption<string>[] = [{ label: "All", value: "" }, ...allMtBillingTypeOptions];

interface Props {
  reportId?: string;
}

const MtUsageForClientBillingReportsTab: React.FC<Props> = ({ reportId }) => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [hasRunningReports, setHasRunningReports] = React.useState(false);

  const now = new Date();
  const defaultFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const initialValues: FormValues = React.useMemo(
    () => ({
      from: dayjs(defaultFrom).format(DATE_PICKER_FORMAT),
      to: dayjs(now).format(DATE_PICKER_FORMAT),
      billingType: "" as MtBillingType | ""
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    mutate: createReport,
    isLoading: isCreating,
    error: createError,
    reset: resetCreateError
  } = useMutation(MetricsService.createClientMtUsageReport, {
    onSuccess: () => {
      queryClient.invalidateQueries(["listClientMtUsageReports"]);
      setDialogOpen(false);
    }
  });

  const handleCreateReportClick = React.useCallback(() => {
    resetCreateError();
    setDialogOpen(true);
  }, [resetCreateError]);

  const handleCloseDialog = React.useCallback(() => {
    if (!isCreating) {
      setDialogOpen(false);
    }
  }, [isCreating]);

  const handleRunningReportsChange = React.useCallback((hasRunning: boolean) => {
    setHasRunningReports(hasRunning);
  }, []);

  const validate = React.useCallback((values: FormValues) => {
    const fromValue = dayjs(values[FieldNames.from], DATE_PICKER_FORMAT);
    const toValue = dayjs(values[FieldNames.to], DATE_PICKER_FORMAT);
    const errors: Record<string, string> = {};
    if (!values[FieldNames.from]) {
      errors[FieldNames.from] = "Start date is required";
    }
    if (!values[FieldNames.to]) {
      errors[FieldNames.to] = "End date is required";
    }
    if (toValue.isBefore(fromValue)) {
      errors[FieldNames.to] = "Start date must be before end date";
    } else if (toValue.subtract(365, "day").isAfter(fromValue)) {
      errors[FieldNames.to] = "Search range cannot exceed 365 days";
    }
    return errors;
  }, []);

  const onSubmit = React.useCallback(
    (values: FormValues) => {
      const fromValue = dayjs
        .tz(values[FieldNames.from], DATE_PICKER_FORMAT, "America/Los_Angeles")
        .startOf("day")
        .toISOString();
      const toValue = dayjs
        .tz(values[FieldNames.to], DATE_PICKER_FORMAT, "America/Los_Angeles")
        .endOf("day")
        .toISOString();
      createReport({
        requestBody: {
          fromDate: fromValue,
          toDate: toValue,
          billingType: (values[FieldNames.billingType] || undefined) as MtBillingType | undefined
        }
      });
    },
    [createReport]
  );

  return (
    <>
      {reportId ? (
        <MtUsageForClientBillingReportDetailsPage reportId={reportId} />
      ) : (
        <>
          <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body1" color="text.secondary">
              Generate and view MT usage reports for client billing. Reports are generated asynchronously and can be
              downloaded when complete.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateReportClick}
              disabled={isCreating || hasRunningReports}
              sx={{ ml: 2, whiteSpace: "nowrap" }}
            >
              {isCreating
                ? "Creating..."
                : hasRunningReports
                  ? "Report Generation in Progress..."
                  : "Generate New Report"}
            </Button>
          </Box>

          <MtUsageReportsList onRunningReportsChange={handleRunningReportsChange} />
          {createError && <SnackbarApiError error={createError} />}
        </>
      )}

      {dialogOpen && (
        <Dialog open={true} onClose={handleCloseDialog} fullWidth={true} maxWidth="sm">
          <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit} validateOnMount={false}>
            <Form noValidate={true} autoComplete="off" autoCorrect="off">
              <DialogTitle>Generate MT Usage Report</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Select the date range and billing type for the report. The report will be generated asynchronously and
                  may take several minutes to complete.
                </Typography>
                <Stack spacing={3} sx={{ mt: 1 }}>
                  <DatePickerField
                    name={FieldNames.from}
                    label="Start date"
                    variant="outlined"
                    size="small"
                    disableFuture={true}
                  />
                  <DatePickerField
                    name={FieldNames.to}
                    label="End date"
                    variant="outlined"
                    size="small"
                    disableFuture={true}
                  />
                  <Box sx={{ minWidth: 220 }}>
                    <SelectOneField
                      name={FieldNames.billingType}
                      label="Billing Type"
                      options={billingTypeOptions}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Stack>
                {createError && (
                  <Box sx={{ mt: 2 }}>
                    <ApiErrorMessage apiError={createError} />
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <LoadingButton variant="contained" isLoading={isCreating} color="primary">
                  Generate Report
                </LoadingButton>
                <Button onClick={handleCloseDialog} disabled={isCreating}>
                  Cancel
                </Button>
              </DialogActions>
            </Form>
          </Formik>
        </Dialog>
      )}
    </>
  );
};

export { MtUsageForClientBillingReportsTab };
